import { type UseModalState } from "@clipboard-health/ui-components";
import { APP_V2_USER_EVENTS, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { getValueInUsd } from "@src/appV2/lib/Money/utils/currency";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { useQueryClient } from "@tanstack/react-query";
import { max, parseISO } from "date-fns";
import { useEffect } from "react";

import { calculateMinutesElapsed } from "../../ArrivalTime/calculateMinutesElapsed";
import { invalidateShifts } from "../../invalidateShifts";
import {
  SHIFT_DISCOVERY_SHIFT_AUCTION_PLACE_BID_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_MODAL_PATH,
} from "../../ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "../../ShiftDiscovery/useShiftModalsDataContext";
import { getScheduledShiftDurationInMinutes } from "../../utils/getScheduledShiftDuration";
import { getShiftWorkDurationInHours } from "../../utils/getShiftWorkDuration";
import { invalidateShiftModal } from "../Open/useGetShiftModal";
import { type ModalShiftData } from "../types";
import { useInstantBookShiftAuction } from "./api/useInstantBookShiftAuction";
import { ConfirmAuctionInstantBookBottomSheet } from "./ConfirmAuctionInstantBookBottomSheet";
import { SHIFT_AUCTION_LABEL } from "./constants";

export interface ConfirmAuctionInstantBookBottomSheetContainerProps {
  modalState: UseModalState;
  shift: ModalShiftData;
}

export function ConfirmAuctionInstantBookBottomSheetContainer(
  props: ConfirmAuctionInstantBookBottomSheetContainerProps
) {
  const { modalState, shift } = props;

  const {
    id: shiftId,
    workplace,
    shiftAuction,
    shiftAuctionBid,
    attributes: { start, end, isInstantPay },
  } = shift;

  const {
    requiresLunchBreak,
    name: workplaceName,
    location: { timezone },
  } = workplace.attributes;

  const startDate = parseISO(start);
  const endDate = parseISO(end);

  useLogEffect(
    APP_V2_USER_EVENTS.SHIFT_AUCTION_INSTANT_BOOK_VIEWED,
    { shiftId },
    { enabled: modalState.modalIsOpen }
  );

  const { navigateToModal, computedArrivalData } = useShiftModalsDataContext();
  const { showInfoToast, showSuccessToast, showErrorToast } = useToast();
  const queryClient = useQueryClient();

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId: workplace.id,
  });

  const { mutate: instantBook, isLoading: isInstantBookLoading } = useInstantBookShiftAuction({
    onSuccess: async () => {
      showSuccessToast("Shift booked!");
      await Promise.all([
        invalidateShiftModal(queryClient, shiftId),
        invalidateShifts(queryClient),
      ]);
      navigateToModal(SHIFT_DISCOVERY_SHIFT_MODAL_PATH, {
        pathParams: { shiftId },
        clearExistingSearchParams: true,
      });
    },
    onError: () => {
      showErrorToast("Error booking shift");
    },
  });

  const handleBack = () => {
    navigateToModal(SHIFT_DISCOVERY_SHIFT_AUCTION_PLACE_BID_MODAL_PATH, {
      pathParams: { shiftId },
    });
  };

  useEffect(() => {
    if (!shiftAuction) {
      showInfoToast(`No ${SHIFT_AUCTION_LABEL} auction, returning to shift details`);
      logEvent(APP_V2_USER_EVENTS.SHIFT_AUCTION_CONFIRM_INSTANT_BOOK_NO_AUCTION, { shiftId });
      navigateToModal(SHIFT_DISCOVERY_SHIFT_MODAL_PATH, {
        pathParams: { shiftId },
        clearExistingSearchParams: true,
      });
      return;
    }

    if (!shiftAuction.attributes.instantBookPay) {
      showInfoToast("Instant booking is unavailable, returning to shift details");
      navigateToModal(SHIFT_DISCOVERY_SHIFT_MODAL_PATH, {
        pathParams: { shiftId },
        clearExistingSearchParams: true,
      });
    }
  }, [shiftAuction, shiftId, navigateToModal, showInfoToast]);

  if (!shiftAuction) {
    return null;
  }

  const { instantBookPay } = shiftAuction.attributes;

  if (!instantBookPay) {
    return null;
  }

  const instantBookPayInUsd = getValueInUsd(instantBookPay.amountInMinorUnits);

  const scheduledShiftDurationInHours = getScheduledShiftDurationInMinutes({ start, end }) / 60;
  const minutesElapsed = calculateMinutesElapsed(
    startDate,
    max([shiftAuction.attributes.endTime, new Date()])
  );

  const remainingWorkDurationInHours = getShiftWorkDurationInHours(
    { start, end },
    Boolean(requiresLunchBreak),
    mandatoryBreakDurationInMinutes,
    minutesElapsed
  );

  const auctionBidAmount = shiftAuctionBid
    ? getValueInUsd(shiftAuctionBid.attributes.bidAmount.amountInMinorUnits)
    : undefined;

  const handleConfirm = () => {
    instantBook({
      shiftAuctionId: shiftAuction.id,
      bidAmount: instantBookPayInUsd,
      computedArrivalData,
    });
  };

  return (
    <ConfirmAuctionInstantBookBottomSheet
      modalState={modalState}
      workplaceName={workplaceName}
      shiftStart={startDate}
      shiftEnd={endDate}
      scheduledShiftDurationInHours={scheduledShiftDurationInHours}
      breakDurationInMinutes={mandatoryBreakDurationInMinutes}
      requiresBreak={Boolean(requiresLunchBreak)}
      remainingWorkDurationInHours={remainingWorkDurationInHours}
      minutesElapsed={minutesElapsed}
      timezone={timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone}
      auctionEndTime={shiftAuction.attributes.endTime}
      instantBookPayInUsd={instantBookPayInUsd}
      auctionBidAmount={auctionBidAmount}
      isInstantPay={Boolean(isInstantPay)}
      isLoading={isInstantBookLoading}
      onConfirm={handleConfirm}
      onBack={handleBack}
    />
  );
}
